/**
 * SampleBookPage — /studio/sample-book
 *
 * A one-page sales tool. Admin uploads 2–5 pet photos, gives the pet a
 * name, and gets back a fully illustrated mini-book (with a closing "…'s
 * story to be continued…" page) they can show prospects. Same
 * password gate as /studio.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Upload, ImagePlus, Trash2, Sparkles, ArrowRight, BookOpen,
  LogOut, ChevronLeft, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASSWORD_KEY = "snuggle_studio_password";

const btnPrimary =
  "inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md " +
  "bg-[#E6C982] text-[#0F1115] font-semibold text-sm hover:brightness-110 " +
  "disabled:opacity-40 disabled:pointer-events-none transition";
const btnGhost =
  "inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md " +
  "border border-[#1E232D] text-white/80 hover:border-white/30 hover:text-white " +
  "text-xs transition";
const inputCls =
  "w-full bg-[#0F1115] border border-[#1E232D] rounded-md px-3 py-3 text-sm " +
  "text-white placeholder:text-white/30 focus:outline-none focus:border-[#E6C982]/60";

const headers = (pw) => ({ headers: { "x-admin-password": pw } });

export default function SampleBookPage() {
  const [password, setPassword] = useState(() => localStorage.getItem(PASSWORD_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pwCheck, setPwCheck] = useState("");
  const [petName, setPetName] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null); // { job_id, story_id, phase, status }
  const fileRef = useRef(null);

  // Auth check: hit a known admin-guarded endpoint with whatever password
  // we have (persisted from a previous studio session).
  useEffect(() => {
    if (!password) return;
    (async () => {
      try {
        await axios.get(`${API}/studio/stories`, headers(password));
        setAuthed(true);
      } catch {
        setAuthed(false);
        localStorage.removeItem(PASSWORD_KEY);
      }
    })();
  }, [password]);

  const login = async () => {
    try {
      await axios.get(`${API}/studio/stories`, headers(pwCheck));
      localStorage.setItem(PASSWORD_KEY, pwCheck);
      setPassword(pwCheck);
      setAuthed(true);
    } catch {
      toast.error("Wrong password");
    }
  };

  const addFiles = (list) => {
    if (!list?.length) return;
    const incoming = Array.from(list);
    setFiles((prev) => {
      const combined = [...prev, ...incoming].slice(0, 5);
      if (prev.length + incoming.length > 5) {
        toast.error("Max 5 photos");
      }
      return combined;
    });
  };
  const removeFile = (ix) => setFiles((prev) => prev.filter((_, i) => i !== ix));

  const canSubmit = petName.trim().length > 0 && files.length >= 2 && files.length <= 5 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setJob(null);
    try {
      const fd = new FormData();
      fd.append("pet_name", petName.trim());
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/studio/sample-book`, fd, headers(password));
      setJob({ ...r.data, phase: "planning" });
      toast.success("Sample book started — this takes 1–2 minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
      setSubmitting(false);
    }
  };

  // Poll the job while it runs.
  useEffect(() => {
    if (!job?.job_id) return;
    if (job.status === "done" || job.status === "failed") return;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/studio/jobs/${job.job_id}`, headers(password));
        const j = r.data.job || r.data;
        setJob((prev) => ({ ...prev, status: j.status, phase: j?.result?.phase || prev.phase, error: j.error, result: j.result }));
        if (j.status === "done" || j.status === "failed") {
          setSubmitting(false);
          if (j.status === "done") toast.success("Sample book ready");
          else toast.error(j.error || "Sample book failed");
        }
      } catch (e) {
        /* transient — try again */
      }
    }, 3500);
    return () => clearInterval(iv);
  }, [job?.job_id, job?.status, password]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center px-4">
        <div className="w-full max-w-sm border border-[#1E232D] rounded-lg p-6 bg-[#0F1115]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E6C982]/80 mb-2">
            <BookOpen className="w-4 h-4" /> Sample Book
          </div>
          <h1 className="text-2xl font-serif mb-4">Enter admin password</h1>
          <input
            type="password"
            value={pwCheck}
            onChange={(e) => setPwCheck(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className={inputCls}
            data-testid="sample-book-password"
          />
          <button onClick={login} className={btnPrimary + " w-full mt-3"} data-testid="sample-book-login">
            <ArrowRight className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-24">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/studio"
            className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
            data-testid="sample-book-back-to-studio"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Studio
          </Link>
          <button
            onClick={() => {
              localStorage.removeItem(PASSWORD_KEY);
              setAuthed(false);
              setPassword("");
            }}
            className="text-xs text-white/40 hover:text-white/70 inline-flex items-center gap-1"
            data-testid="sample-book-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>

        <div className="text-xs uppercase tracking-[0.3em] text-[#E6C982]/80 mb-2">
          The Snuggle Prints
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif leading-tight mb-3">Sample book</h1>
        <p className="text-white/60 text-sm leading-relaxed mb-8">
          Upload 2 to 5 photos of a pet and give us a name. We&rsquo;ll spin
          up a complete illustrated mini-book (one page per photo, plus a
          closing &ldquo;story to be continued&rdquo; page) so you can show a
          prospect what the final product feels like. Takes 1 to 2 minutes.
        </p>

        {!job ? (
          <div className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1.5 block">Pet&rsquo;s name</label>
              <input
                type="text"
                value={petName}
                onChange={(e) => setPetName(e.target.value)}
                placeholder="Bruno"
                className={inputCls}
                data-testid="sample-book-pet-name"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1.5 block">
                Photos ({files.length}/5) · min 2
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {files.map((f, ix) => (
                  <div key={ix} className="relative aspect-square rounded-lg overflow-hidden border border-[#1E232D]" data-testid={`sample-book-thumb-${ix}`}>
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeFile(ix)}
                      className="absolute top-1 right-1 bg-black/70 hover:bg-red-500/90 text-white p-1 rounded"
                      aria-label="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {files.length < 5 && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-[#1E232D] hover:border-[#E6C982]/60 bg-[#0F1115] flex flex-col items-center justify-center gap-1 text-white/40 hover:text-white transition"
                    data-testid="sample-book-add-photo"
                  >
                    <ImagePlus className="w-6 h-6 text-[#E6C982]" />
                    <span className="text-[10px]">Add</span>
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/heic"
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files);
                  if (fileRef.current) fileRef.current.value = "";
                }}
                data-testid="sample-book-file-input"
              />
            </div>

            <button
              onClick={submit}
              disabled={!canSubmit}
              className={btnPrimary + " w-full"}
              data-testid="sample-book-generate"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {submitting ? "Starting…" : "Generate sample book"}
            </button>
            <p className="text-[11px] text-white/40 leading-relaxed">
              Uses the same illustration style + house voice as a full book.
              Costs a handful of AI credits per run (visible in Studio &gt; the
              story you just made).
            </p>
          </div>
        ) : (
          <SampleBookProgress job={job} password={password} onReset={() => { setJob(null); setFiles([]); setPetName(""); }} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Progress view ------------------------------ */
const PHASES = [
  { key: "planning", label: "Planning pages" },
  { key: "writing_text", label: "Writing page copy" },
  { key: "illustrating", label: "Painting illustrations" },
  { key: "adding_closing", label: "Adding closing page" },
  { key: "done", label: "Done" },
];

function SampleBookProgress({ job, password, onReset }) {
  const phaseIdx = Math.max(0, PHASES.findIndex((p) => p.key === (job.phase || "planning")));
  const isDone = job.status === "done";
  const isFailed = job.status === "failed";

  const [story, setStory] = useState(null);
  useEffect(() => {
    if (!isDone) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/studio/stories/${job.story_id}`, headers(password));
        setStory(r.data.story);
      } catch {
        /* skip preview */
      }
    })();
  }, [isDone, job.story_id, password]);

  const pageCount = story?.pages?.length || 0;
  const illustrated = useMemo(
    () => (story?.pages || []).filter((p) => (p.versions || []).length > 0).length,
    [story],
  );

  return (
    <div className="border border-[#1E232D] rounded-lg bg-[#0F1115] p-5" data-testid="sample-book-progress">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">
          {isDone ? "Sample book ready" : isFailed ? "Something went wrong" : "Building your sample book…"}
        </h3>
        <button onClick={onReset} className={btnGhost} data-testid="sample-book-start-over">
          Start over
        </button>
      </div>

      {/* Phase list */}
      <ol className="space-y-2 mb-5">
        {PHASES.map((p, ix) => {
          const done = ix < phaseIdx || (isDone && ix < PHASES.length);
          const active = ix === phaseIdx && !isDone && !isFailed;
          return (
            <li key={p.key} className="flex items-center gap-3 text-sm" data-testid={`sample-book-phase-${p.key}`}>
              <span
                className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${
                  done ? "bg-[#E6C982] text-[#0F1115]" : active ? "border-2 border-[#E6C982]" : "border border-[#1E232D]"
                }`}
              >
                {done ? "✓" : ""}
              </span>
              <span className={active ? "text-white" : done ? "text-white/70" : "text-white/30"}>
                {p.label}
              </span>
              {active && <Loader2 className="w-3 h-3 animate-spin text-[#E6C982] ml-1" />}
            </li>
          );
        })}
      </ol>

      {isFailed && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2 rounded-md mb-4" data-testid="sample-book-error">
          {job.error || "Job failed"}
        </div>
      )}

      {isDone && story && (
        <div>
          <div className="text-xs text-white/50 mb-3">
            {illustrated}/{pageCount} pages illustrated
            {story.pages?.length > 0 && ` · closing tagline: "${story.pages[story.pages.length - 1]?.text}"`}
          </div>
          {/* Page thumbnails */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5" data-testid="sample-book-preview-grid">
            {(story.pages || []).map((p, ix) => {
              const versions = p.versions || [];
              const chosen = versions.find((v) => v.id === p.selected_version_id) || versions[versions.length - 1];
              return (
                <div key={p.id} className="aspect-square bg-[#0B0D12] rounded overflow-hidden border border-[#1E232D] relative" data-testid={`sample-book-preview-${ix}`}>
                  {chosen?.url ? (
                    <img src={chosen.url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-white/30 text-center px-2">
                      {p.text || p.title || "no image"}
                    </div>
                  )}
                  <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] px-1.5 rounded">
                    {ix + 1}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/studio?story=${job.story_id}`}
              className={btnPrimary}
              data-testid="sample-book-open-in-studio"
            >
              <BookOpen className="w-4 h-4" /> Open in Studio
            </Link>
            <button onClick={onReset} className={btnGhost} data-testid="sample-book-make-another">
              Make another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
